// Package imports:
import React from 'react';
import { Form, useFormikContext } from 'formik';
// Component imports:
import Checkbox from '../../../ui-elements/Input/Formik/Checkbox';
import Input from '../../../ui-elements/Input/Formik/Input';
import DropSelectSearch from '../../../ui-elements/DropSelect/Formik/DropSelectSearch';
import { DatePickerField } from '../../../ui-elements/DatePicker/Formik/DatePicker';
import DropSelect from '../../../ui-elements/DropSelect/DropSelect';
import Button from '../../../ui-elements/Button/Button';
import { FormObserver } from './IndividualFormObserver';
// Type imports:
import { AddIndividualFormValues, ServiceCompanyEmployee } from "../../../types/DueDiligenceTypes";
// Static data imports:
import { CountryList } from '../../../assets/staticData/CountryList';


interface IProps {
    values: AddIndividualFormValues;
    employees: ServiceCompanyEmployee[];
    closeModal(): void;
    disableSubmit: boolean;
}

export const AddIndividualForm: React.FC<IProps> = ({
    values,
    employees,
    closeModal,
    disableSubmit
}) => {
    const { setFieldValue, getFieldMeta, resetForm } = useFormikContext<AddIndividualFormValues>();
    const countryCodeFieldMeta = getFieldMeta("country");

    return (
        <Form>
            <FormObserver />
            <div className="form__body">
                <div className="form__row">
                    <div className="form__col">
                        <Checkbox
                            label="Erlendur eða ekki með rafræn skilríki"
                            name="foreign"
                            id="foreign"
                            value={values.foreign.toString()}
                            onClick={() => resetForm({ values: { ...values } })} // reset validation state when toggling foreign
                        />
                    </div>
                </div>

                {values.foreign === false ? (
                    <div className="non-foreign-form">
                        <div className="form__row">
                            <div className="form__col">
                                <Input
                                    label="Kennitala"
                                    inputType='ssnFormat'
                                    format='######-####'
                                    name="ssn"
                                    id="ssn"
                                    value={values.ssn}
                                />
                            </div>
                            <div className="form__col">
                                <Input
                                    label="Nafn"
                                    name="name"
                                    id="name"
                                    value={values.name}
                                />
                            </div>
                        </div>
                        <div className="form__row">
                            <div className="form__col">
                                <Input
                                    label="Netfang"
                                    name="email"
                                    id="email"
                                    value={values.email}
                                />
                            </div>
                            <div className="form__col">
                                <DropSelect
                                    clearInput={true}
                                    label="Umsjónaraðili (valkvætt)"
                                    options={employees.map(emp => ({
                                        id: String(emp.webUserId),
                                        label: emp.fullname,
                                        value: String(emp.webUserId)
                                    }))}
                                    defaultValue={null}
                                    onChange={val => setFieldValue("supervisorId", val)}
                                    size="lg"
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className='foreign-individual-form'>
                        
                        {/* Name */}
                        <Input label="Nafn" name="name" id="name" value={values.name} />
                        
                        {/* Date of Birth */}
                        <DatePickerField
                            label="Fæðingardagur"
                            name="dateOfBirth"
                            id="dateOfBirth"
                            size="lg"
                            maxDate={new Date()}
                            popperPlacement="top"
                            showYearDropdown
                            showMonthDropdown
                            max_width
                        />
                        
                        <div className="address-area">
                            {/* Address */}
                            <Input label="Heimilisfang" name="address" id="address" value={values.address} />
                            
                            {/* Postal Code */}
                            <Input label="Póstnúmer" name="areaCode" id="areaCode" value={values.areaCode} />
                        </div>
                        
                        {/* Region */}
                        <Input label="Sveitarfélag" name="region" id="region" value={values.region} />

                        {/* Country */}
                        <DropSelectSearch
                            usePortal={true}
                            error={
                                countryCodeFieldMeta.touched && countryCodeFieldMeta.error ? countryCodeFieldMeta.error : undefined
                            }
                            selectSize="lg"
                            label="Búsetuland"
                            name="country"
                            id="country"
                            options={CountryList}
                            defaultValue={values.country}
                            onSelectChange={(cc: string) => setFieldValue("country", cc)}
                        />
                        
                        {/* Nationality */}
                        <DropSelectSearch
                            usePortal={true}
                            error={
                                countryCodeFieldMeta.touched && countryCodeFieldMeta.error ? countryCodeFieldMeta.error : undefined
                            }
                            selectSize="lg"
                            label="Ríkisfang"
                            name="nationality"
                            id="nationality"
                            options={CountryList}
                            defaultValue={values.nationality}
                            onSelectChange={(cc: string) => setFieldValue("nationality", cc)}
                        />

                        {/* Email */}
                        <Input label="Netfang" name="email" id="email" value={values.email} />

                        {/* Supervisor */}
                        <DropSelect
                            clearInput={true}
                            usePortal={true}
                            label="Umsjónaraðili (valkvætt)"
                            options={employees.map(emp => ({
                                id: String(emp.webUserId),
                                label: emp.fullname,
                                value: String(emp.webUserId)
                            }))}
                            defaultValue={null}
                            onChange={val => setFieldValue("supervisorId", val)}
                            size="lg"
                        />

                    </div>
                )}
            </div>
            <div className="form__actions">
                <Button
                    type="button"
                    buttonType="secondary"
                    size="lg"
                    onClick={closeModal}
                >
                    Hætta við
                </Button>
                <Button
                    type="submit"
                    buttonType="primary"
                    size="lg"
                    disabled={disableSubmit}
                >
                    Staðfesta
                </Button>
            </div>
        </Form>
    )
}

export const validate = (values: AddIndividualFormValues) => {
    const errors: { [key: string]: string } = {};
    if (!values.foreign) {
        if (!values.ssn) {
            errors.ssn = 'Kennitala er nauðsynleg';
        } else if (!/^\d{10}$/.test(values.ssn)) {
            errors.ssn = 'Kennitala verður að vera 10 tölustafir';
        }
        if (!values.email) errors.email = 'Netfang er nauðsynlegt';
    } else {
        if (!values.name) errors.name = 'Nafn er nauðsynlegt';
        if (!values.address) errors.address = 'Heimilisfang er nauðsynlegt';
        if (!values.areaCode) errors.areaCode = 'Póstnúmer er nauðsynlegt';
        if (!values.region) errors.region = 'Sveitarfélag er nauðsynlegt';
        if (!values.country) errors.country = 'Land er nauðsynlegt';
        if (!values.email) errors.email = 'Netfang er nauðsynlegt';
        if (!values.dateOfBirth) errors.dateOfBirth = 'Fæðingardagur er nauðsynlegur';
    }

    return errors;
};